import { makeAutoObservable, observable, entries, values, get as mGet, set as mSet, remove as mRemove, autorun, runInAction } from 'mobx'
import { isNothing, isSomething, isString, isNumber } from '@Utils/Utils'
import get from 'lodash/get'
import { APIDecimal, APIDecimals } from '@Services/APIs/APIDecimal'
import { staticFooter } from './MainStore'
import { decimalController } from '@GlobalHelpers'
import { language } from '@Language'
import DecimalConverter from './DecimalConverter'
import { loge } from '@Utils/PikaLog'

export const mapProductSource = observable.map()

class StoreProduct {
    sortDirection = 'asc'
    isInitialized = false

    category = null
    hot = []
    top10 = []
    cart = {}
    country = {}
    limitedQty = {
        min: 0,
        max: 999
    }
    shop = []

    starterKits = []
    categories = []

    warehouseItems = []
    warehouseSelectedValue = 'Main'

    priceCurrency = ''

    updatingPrice = false

    // * Products Management
    get productsList() {
        const list = []
        const map = mapProductSource.entries()
        for (let i = 0; i < mapProductSource.size; i++) {
            list.push(map.next().value)
        }

        return list
    }
    get products() {
        return mapProductSource
    }
    get productsKeys() {
        return mapProductSource.keys()
    }
    get productsValues() {
        return mapProductSource.values()
    }
    get productsEntries() {
        return mapProductSource.entries()
    }
    getProduct(id) {
        
        let convertedPriceProduct = this.shop.find(item => item.item_code === id)
        let product = mapProductSource.get(id)
        if (convertedPriceProduct && product) {
            product.pv_converted = convertedPriceProduct.pv_converted
            product.price_converted = convertedPriceProduct.price_converted
        }
        return product
    }
    setProduct(id, value) {
        return mapProductSource.set(id, value)
    }
    removeProduct(id) {
        return mapProductSource.delete(id)
    }
    hasProduct(id) {
        return mapProductSource.has(id)
    }

    /** Get price from product which already finalized diffent levels of price.
     * @param {string} productID The specified product id.
     * @return {number} The result of product price.
     */
    getProductPrice(productID) {
        if (this.hasProduct(productID)) {
            return get(this.getProduct(productID), 'price', undefined)
        } else {
            return undefined
        }
    }

    get cartList() {
        const list = []
        const map = entries(this.cart)
        map.map(([key, qty]) => {
            list.push({ id: key, qty: parseInt(qty) })
        })
        return list
    }
    get cartValues() {
        return values(this.cart)
    }
    get cartEntries() {
        return entries(this.cart)
    }
    // * -------------------
    getCartTotal() {
        let total = 0
        entries(this.cart).map(([key, qty]) => {
            total += this.getProductPrice(key) * qty
        })
        return total
    }
    getCartTotalPV() {
        let totalPV = 0
        entries(this.cart).map(([key, qty]) => {
            if (this.getProduct(key)) {
                const pv = this.getProduct(key).pv
                if (pv) {
                    totalPV += parseInt(pv) * qty
                }
            }
        })
        return totalPV
    }
    getCartItemTotal(key) {
        return this.getProductPrice(key) * this.getCartItemQty(key)
    }
    getCartItemTotalPV(key) {
        return this.getCartItem(key).pv * this.getCartItemQty(key)
    }
    isCartEmpty() {
        return isNothing(this.cart)
    }
    // clear
    clearCart() {
        this.cart = {}
    }
    getCartItem(itemCode) {
        return mGet(this.cart, itemCode)
    }
    getCartItemQty(itemCode) {
        if (this.isCartEmpty()) return 0
        const qty = mGet(this.cart, itemCode)
        if (isSomething(qty)) {
            return parseInt(qty)
        } else {
            return 0
        }
    }
    // add
    addToCart(itemCode, qty = 1) {
        // limited min qty
        const currentQty = this.getCartItemQty(itemCode)
        let newQty = currentQty + qty
        if (newQty >= this.limitedQty.max) {
            newQty = this.limitedQty.max
        }
        mSet(this.cart, { [itemCode]: newQty })
    }
    // remove
    removeFromCart(itemCode, qty = 1) {
        // limited min qty
        const currentQty = this.getCartItemQty(itemCode)
        let newQty = currentQty - qty
        if (newQty <= this.limitedQty.min) {
            newQty = this.limitedQty.min
        }
        if (newQty > this.limitedQty.min) {
            mSet(this.cart, { [itemCode]: newQty })
        } else {
            mRemove(this.cart, itemCode)
        }
    }
    // direct change
    assignToCart(itemCode, qty) {
        let newQty = qty
        let acceptedItemCode = itemCode
        if (isNumber(itemCode)) {
            acceptedItemCode = `${itemCode}`
        }
        if (isString(acceptedItemCode)) {
            // limited qty
            if (newQty >= this.limitedQty.max) {
                newQty = this.limitedQty.max
            }
            if (newQty <= this.limitedQty.min) {
                newQty = this.limitedQty.min
            }
            if (newQty <= 0) {
                mRemove(this.cart, itemCode)
            } else {
                mSet(this.cart, { [itemCode]: newQty })
            }
        }
    }

    async updateFormatPrice(prices) {
        try {
            const response = await DecimalConverter.multipleConvert(prices)
            this.shop.map(item => {
                const itemConvertedPrice = response.find(data => data.unit !== 'PV' && data.key === item.item_code)
                if (itemConvertedPrice) {
                    item.price_converted = itemConvertedPrice.number
                }
                const itemConvertedPv = response.find(data => data.unit === 'PV' && data.key === item.item_code)
                if (itemConvertedPv) {
                    item.pv_converted = itemConvertedPv.number
                }

                return item
            })
            this.updatingPrice = false
        } catch (e) {
            loge(e)
        }
    }

    constructor() {
        makeAutoObservable(this)

        autorun(r => {
            if (this.shop.length > 0 && get(staticFooter, `footerGeneral.currency_code.english`, null)) {
                let prices = []
                this.updatingPrice = true
                this.shop.map(item => {
                    prices.push({
                        number: item.price,
                        unit: get(staticFooter, `footerGeneral.currency_code.english`, ''),
                        key: item.item_code
                    })
                    if (item.pv) {
                        prices.push({
                            number: item.pv,
                            unit: 'PV',
                            key: item.item_code
                        })
                    }
                })

                runInAction(() => {
                    this.priceCurrency = get(staticFooter, `footerGeneral.currency_code.english`, '')
                    this.updateFormatPrice(prices)
                })
                r.dispose()
            }
        })
    }
}

export const storeProduct = new StoreProduct()
